import React, { useState, useEffect, useRef } from 'react';
import RestaurantView from './RestaurantView';
import { getRestaurant, setProposalSorted, listenToPublicUser } from '../services/DataStore';
import { useAuth } from '../contexts/AuthContext';
import { debug } from '../utils/debug';

class RestaurantMergeSorter {
  constructor(restaurants, onComparison, onComplete, onProgress) {
    this.restaurants = [...restaurants];
    this.onComparison = onComparison;
    this.onComplete = onComplete;
    this.onProgress = onProgress;
    this.totalComparisons = this.calculateTotalComparisons(restaurants.length);
    this.completedComparisons = 0;
    this.isRunning = false;
  }

  calculateTotalComparisons(n) {
    // For merge sort, we need n*log(n) - n + 1 comparisons in the best case
    // dividing by 2 because we are comparing two restaurants at a time
    return Math.floor((n * Math.log2(n) - n + 1) / 2);
  }

  async startSorting() {
    this.isRunning = true;
    const sorted = await this.mergeSort(this.restaurants);
    if (this.isRunning) {
      this.onComplete(sorted, this.completedComparisons);
    }
  }

  stopSorting() {
    this.isRunning = false;
  }

  async mergeSort(array) {
    if (!this.isRunning) return null;
    if (array.length <= 1) return array;

    const mid = Math.floor(array.length / 2);
    const left = await this.mergeSort(array.slice(0, mid));
    if (!this.isRunning) return null;
    const right = await this.mergeSort(array.slice(mid));
    if (!this.isRunning) return null;

    return this.merge(left, right);
  }

  async merge(left, right) {
    if (!this.isRunning) return null;
    
    const result = [];
    let i = 0, j = 0;

    while (i < left.length && j < right.length) {
      if (!this.isRunning) return null;

      const selected = await this.onComparison(left[i], right[j]);
      this.completedComparisons++;
      this.onProgress(this.completedComparisons, this.totalComparisons);

      if (selected === left[i]) {
        result.push(left[i]);
        i++;
      } else {
        result.push(right[j]);
        j++;
      }
    }

    // Add remaining elements
    if (this.isRunning) {
      while (i < left.length) {
        result.push(left[i]);
        i++;
      }
      while (j < right.length) {
        result.push(right[j]);
        j++;
      }
    }

    return result;
  }
}

const VotingView = ({ proposal, onClose, onVotingComplete }) => {
  const { currentUser } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [sorter, setSorter] = useState(null);
  const [currentComparison, setCurrentComparison] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [recentlyBlocked, setRecentlyBlocked] = useState(null);
  const recentlyBlockedRef = useRef(null);

  // Get the current meal from the proposal
  const currentMeal = proposal.getCustomMeal();

  // Helper function to check if a restaurant is blocked
  const isRestaurantBlocked = (restaurant) => {
    if (!restaurant) return false;
    // Check both the userData and recently blocked state
    return (userData?.trashedrestaurants?.includes(restaurant.id) || false) || 
           (recentlyBlockedRef.current === restaurant.id);
  };

  // Helper function to handle blocked restaurant in comparison
  const handleBlockedRestaurant = (restaurant1, restaurant2) => {
    const blocked1 = isRestaurantBlocked(restaurant1);
    const blocked2 = isRestaurantBlocked(restaurant2);

    debug.log('Checking blocked restaurants:', {
      restaurant1: restaurant1?.id,
      restaurant2: restaurant2?.id,
      blocked1,
      blocked2,
      recentlyBlocked: recentlyBlockedRef.current,
      userDataTrashed: userData?.trashedrestaurants
    });

    if (blocked1 && !blocked2) {
      debug.log('Auto-selecting restaurant2 as restaurant1 is blocked');
      return restaurant2;
    } else if (!blocked1 && blocked2) {
      debug.log('Auto-selecting restaurant1 as restaurant2 is blocked');
      return restaurant1;
    }
    debug.log('No auto-selection needed - both or neither are blocked');
    return null;
  };

  // Helper function to check if a restaurant is a favorite
  const isRestaurantFavorite = (restaurant) => {
    if (!userData || !restaurant) return false;
    return userData.favoriterestaurants?.includes(restaurant.id) || false;
  };

  // Listen to user updates
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = listenToPublicUser(currentUser.uid, (updatedUser) => {
      debug.log('User data updated:', {
        userId: updatedUser?.id,
        trashedRestaurants: updatedUser?.trashedrestaurants
      });
      
      setUserData(updatedUser);
      
      // If we have a current comparison, check for blocked restaurants
      if (currentComparison) {
        debug.log('Checking current comparison after user update:', {
          restaurant1: currentComparison.restaurant1?.id,
          restaurant2: currentComparison.restaurant2?.id
        });
        const autoSelected = handleBlockedRestaurant(
          currentComparison.restaurant1,
          currentComparison.restaurant2
        );
        if (autoSelected) {
          debug.log('Auto-selecting restaurant after user update:', autoSelected.id);
          handleRestaurantSelect(autoSelected);
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser, currentComparison]);

  useEffect(() => {
    const loadRestaurants = async () => {
      setIsLoading(true);
      try {
        const loadedRestaurants = await Promise.all(
          proposal.restaurantpool.map(id => getRestaurant(id))
        );
        const validRestaurants = loadedRestaurants.filter(r => r !== null);
        setRestaurants(validRestaurants);

        if (validRestaurants.length === 1) {
          // If there's only one restaurant, immediately complete voting
          handleSortingComplete(validRestaurants, 0);
        } else if (validRestaurants.length >= 2) {
          const newSorter = new RestaurantMergeSorter(
            validRestaurants,
            handleComparison,
            handleSortingComplete,
            handleProgressUpdate
          );
          setSorter(newSorter);
          newSorter.startSorting();
        }
      } catch (error) {
        console.error('Error loading restaurants:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadRestaurants();

    return () => {
      if (sorter) {
        sorter.stopSorting();
      }
    };
  }, [proposal.restaurantpool]);

  const handleComparison = (restaurant1, restaurant2) => {
    return new Promise((resolve) => {
      debug.log('Starting new comparison:', {
        restaurant1: restaurant1?.id,
        restaurant2: restaurant2?.id
      });

      // Check for blocked restaurants
      const autoSelected = handleBlockedRestaurant(restaurant1, restaurant2);
      if (autoSelected) {
        debug.log('Auto-selecting restaurant in comparison:', autoSelected.id);
        resolve(autoSelected);
        return;
      }

      setCurrentComparison({ restaurant1, restaurant2, resolve });
    });
  };

  const handleSortingComplete = async (sortedRestaurants, totalComparisons) => {
    if (!sortedRestaurants) return;
    
    setIsSubmitting(true);
    setError(null);
    setOpacity(0); // Fade out the restaurant views
    
    try {
      // Convert restaurant objects to IDs
      const sortedRestaurantIds = sortedRestaurants.map(r => r.id);
      
      // Call DataStore to save the sorted list
      await setProposalSorted(proposal.id, sortedRestaurantIds, totalComparisons);
      
      // Call the parent's completion handler
      onVotingComplete(sortedRestaurants);
      
      // Close the voting view
      onClose();
    } catch (error) {
      console.error('Error saving sorted restaurants:', error);
      setError('Failed to save your votes. Please try again.');
      setOpacity(1); // Fade back in if there's an error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProgressUpdate = (completed, total) => {
    setProgress(completed);
  };

  const handleRestaurantSelect = (selectedRestaurant) => {
    if (isAnimating || !currentComparison) return;
    
    setIsAnimating(true);
    setOpacity(0);

    setTimeout(() => {
      currentComparison.resolve(selectedRestaurant);
      setCurrentComparison(null);
      
      setTimeout(() => {
        setOpacity(1);
        setIsAnimating(false);
      }, 50);
    }, 300);
  };

  const handleBlock = async (restaurantId) => {
    debug.log('Restaurant blocked:', restaurantId);
    // Set both the state and ref for immediate access
    setRecentlyBlocked(restaurantId);
    recentlyBlockedRef.current = restaurantId;
    
    // Force a re-check of the current comparison
    if (currentComparison) {
      debug.log('Re-checking comparison after block:', {
        restaurant1: currentComparison.restaurant1?.id,
        restaurant2: currentComparison.restaurant2?.id
      });
      const autoSelected = handleBlockedRestaurant(
        currentComparison.restaurant1,
        currentComparison.restaurant2
      );
      if (autoSelected) {
        debug.log('Auto-selecting restaurant after block:', autoSelected.id);
        handleRestaurantSelect(autoSelected);
      }
    }

    // Clear both the state and ref after a short delay
    setTimeout(() => {
      setRecentlyBlocked(null);
      recentlyBlockedRef.current = null;
    }, 1000);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const currentPair = currentComparison 
    ? [currentComparison.restaurant1, currentComparison.restaurant2]
    : restaurants.slice(currentIndex, currentIndex + 2);

  const totalComparisons = sorter ? sorter.calculateTotalComparisons(restaurants.length) : 0;
  const progressPercentage = Math.min(95, (progress / totalComparisons) * 100);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-[350px] h-[750px] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Compare Restaurants</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Error Toast */}
        {error && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
            {error}
          </div>
        )}

        {/* Restaurant Comparison */}
        <div className="flex-1 flex flex-col bg-black">
          {/* Top Restaurant */}
          <div 
            id="top-restaurant-view"
            className="h-[340px] transition-opacity duration-300"
            style={{ opacity }}
            onClick={() => handleRestaurantSelect(currentPair[0])}
          >
            <RestaurantView
              restaurant={currentPair[0]}
              isInfoWindow={true}
              onClose={() => {}}
              isFavorite={isRestaurantFavorite(currentPair[0])}
              onFavoriteChange={(restaurantId, isFavorite) => {
                // Handle favorite change if needed
              }}
              onBlock={handleBlock}
              meal={currentMeal}
            />
          </div>

          {/* Progress Bar */}
          {sorter && (
            <div 
              className="px-4 py-2 bg-black z-10 transition-opacity duration-300"
              style={{ opacity }}
            >
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-accent h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Bottom Restaurant */}
          {currentPair[1] && (
            <div 
              id="bottom-restaurant-view"
              className="h-[340px] transition-opacity duration-300"
              style={{ opacity }}
              onClick={() => handleRestaurantSelect(currentPair[1])}
            >
              <RestaurantView
                restaurant={currentPair[1]}
                isInfoWindow={true}
                onClose={() => {}}
                isFavorite={isRestaurantFavorite(currentPair[1])}
                onFavoriteChange={(restaurantId, isFavorite) => {
                  // Handle favorite change if needed
                }}
                onBlock={handleBlock}
                meal={currentMeal}
              />
            </div>
          )}

          {/* Loading Spinner */}
          {isSubmitting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20">
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VotingView; 